import type { SalesResearchRun } from '../../../api/salesResearch';

const PHASE_LABEL: Record<string, string> = {
  queued: 'Queued',
  researching: 'Researching',
  cross_checking: 'Cross-checking',
  synthesizing: 'Writing',
};

interface Props {
  run: SalesResearchRun | null;
  triggerError?: Error | null;
}

export function ResearchStatusPill({ run, triggerError }: Props) {
  if (triggerError) {
    return (
      <Container tone="error">
        <strong>Research didn't start.</strong> {triggerError.message}
      </Container>
    );
  }
  if (!run) return null;

  if (run.status === 'failed') {
    return (
      <Container tone="error">
        <strong>Research failed.</strong> {run.errorMessage ?? 'No error message recorded.'}
      </Container>
    );
  }

  if (run.status === 'cancelled') {
    return (
      <Container tone="warn">
        <strong>Cancelled.</strong> This run was stopped before it finished.
      </Container>
    );
  }

  if (run.status === 'ready') {
    return (
      <Container tone="success">
        <strong>Ready.</strong> {formatElapsed(run.startedAt, run.finishedAt)}
        {run.costUsd ? ` · ${formatCostUsd(run.costUsd)} USD` : ''}
      </Container>
    );
  }

  return (
    <Container tone="info">
      <strong>{PHASE_LABEL[run.status] ?? 'Running'}…</strong>{' '}
      {run.progressMessage ?? 'Working'} · {formatElapsed(run.startedAt, null)}
      {/* Deep research alone runs for minutes; say so rather than leaving
          someone wondering whether it has hung. */}
      <span className="block text-body-xs text-text-secondary mt-0.5">
        A full run usually takes 5 to 7 minutes. You can leave this page.
      </span>
    </Container>
  );
}

function Container({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'info' | 'success' | 'warn' | 'error';
}) {
  const palette: Record<typeof tone, string> = {
    info: 'border-info/20 bg-info-light text-info-dark',
    success: 'border-success/20 bg-success-light text-success-dark',
    warn: 'border-warning/20 bg-warning-light text-warning-dark',
    error: 'border-error/20 bg-error-light text-error-dark',
  };
  return <div className={`rounded-md border px-3 py-1.5 text-body-sm ${palette[tone]}`}>{children}</div>;
}

/** A run's API cost, always "$0.42": two decimals, no "~" (it is the billed estimate either way). */
export function formatCostUsd(cost: string | number): string {
  const n = Number(cost);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '';
}

export function formatElapsed(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt) return '0s';
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}
